import {
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Skeleton,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FC, SyntheticEvent, useState } from 'react';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { useSelector } from 'react-redux';
import { selectFullDeviceInfo, selectGetFullDeviceInfoStatus } from '../store/selectors';
import { DeviceFullInfo, DevicesType } from '../types';
import ImageWithSkeleton from '@shared/components/others/ImageWithSkeleton/ImageWithSkeleton';
import { RequestStatusEnum } from '@shared/types';
import { toTitleCase } from '@shared/utils/utilsOld';
import Credentials from './Credentials';

const fieldsToShowInDialog: (keyof DeviceFullInfo)[] = [
    'pk',
    'name',
    'office',
    'location',
    'position',
    'rack',
    'ports',
    'traffic_generators',
    'comment',
    'netbox',
];

const MAP_DEVICE_NAME_TO_CUSTOM_IMAGE_HEIGHT: Record<string, string> = {
    X10: '300px',
    C150: '120px',
    'B50-b': '120px',
};

const layoutSx = {
    display: 'flex',
    gap: 4,
    justifyContent: 'space-between',
    flex: 1,
};

interface BookingDeviceFullInfoDialogProps {
    open: boolean;
    onClose: (e: SyntheticEvent) => void;
}

const BookingDeviceFullInfoDialog: FC<BookingDeviceFullInfoDialogProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const fullDeviceInfo = useSelector(selectFullDeviceInfo);
    const getFullDeviceInfoStatus = useSelector(selectGetFullDeviceInfoStatus);

    return (
        <Dialog
            PaperProps={{ sx: { width: 640, maxWidth: 640 } }}
            onClose={onClose}
            open={open}
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle variant="h4">{t('device_information')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {getFullDeviceInfoStatus === RequestStatusEnum.Loading && (
                        <Box sx={{ position: 'relative', width: '100%', height: '80px' }}>
                            <Skeleton
                                variant="rectangular"
                                width="100%"
                                height="80px"
                                sx={{ position: 'absolute', top: 0, left: 0 }}
                            />
                        </Box>
                    )}
                    {getFullDeviceInfoStatus === RequestStatusEnum.Success && fullDeviceInfo && (
                        <ImageWithSkeleton
                            src={fullDeviceInfo.image}
                            alt={fullDeviceInfo.name}
                            height={MAP_DEVICE_NAME_TO_CUSTOM_IMAGE_HEIGHT[fullDeviceInfo.name] || '80px'}
                            style={{ objectFit: fullDeviceInfo.type === 'dut' ? 'cover' : 'contain' }}
                        />
                    )}
                    {(getFullDeviceInfoStatus === RequestStatusEnum.Success ||
                        getFullDeviceInfoStatus === RequestStatusEnum.Loading) &&
                        fieldsToShowInDialog.map((field) => (
                            <Box key={field} sx={layoutSx}>
                                <Typography sx={{ flex: 1 }}>{t(`booking_${field}`)}</Typography>
                                <Typography sx={{ flex: 1, wordBreak: 'break-all' }}>
                                    {field === 'traffic_generators' && (
                                        <>
                                            {getFullDeviceInfoStatus === RequestStatusEnum.Success &&
                                                (fullDeviceInfo?.traffic_generators ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.5,
                                                            alignItems: 'flex-start',
                                                        }}
                                                    >
                                                        {fullDeviceInfo.traffic_generators.map((trafficGenerator) => (
                                                            <Chip
                                                                color={
                                                                    trafficGenerator.type === DevicesType.IXIA
                                                                        ? 'warning'
                                                                        : 'info'
                                                                }
                                                                sx={{ fontSize: '0.813rem' }}
                                                                key={trafficGenerator.pk}
                                                                label={`${toTitleCase(trafficGenerator.type)} ${trafficGenerator.name} ${trafficGenerator.office}`}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    '—'
                                                ))}
                                            {getFullDeviceInfoStatus === RequestStatusEnum.Loading && (
                                                <Skeleton animation="wave" />
                                            )}
                                        </>
                                    )}
                                    {field !== 'traffic_generators' && (
                                        <>
                                            {getFullDeviceInfoStatus === RequestStatusEnum.Success &&
                                                fullDeviceInfo &&
                                                (field === 'netbox' ? (
                                                    fullDeviceInfo[field] ? (
                                                        <Link
                                                            component="a"
                                                            href={fullDeviceInfo[field]}
                                                            target="_blank"
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            {fullDeviceInfo[field]}
                                                        </Link>
                                                    ) : (
                                                        '—'
                                                    )
                                                ) : (
                                                    fullDeviceInfo[field]
                                                ))}
                                            {getFullDeviceInfoStatus === RequestStatusEnum.Loading && (
                                                <Skeleton animation="wave" />
                                            )}
                                        </>
                                    )}
                                </Typography>
                            </Box>
                        ))}
                    {fullDeviceInfo?.credentials && (
                        <Box sx={layoutSx}>
                            <Typography sx={{ flex: 1 }}>{t('credentials')}</Typography>
                            <DeviceCredentialsModal />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <UtmsButton onClick={onClose}>{t('close')}</UtmsButton>
            </DialogActions>
        </Dialog>
    );
};

export default BookingDeviceFullInfoDialog;

const DeviceCredentialsModal = () => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const fullDeviceInfo = useSelector(selectFullDeviceInfo);

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    return (
        <>
            <UtmsButton onClick={handleOpenModal}>{t('show_credentials')}</UtmsButton>
            <Dialog open={open} onClose={handleCloseModal}>
                <DialogTitle variant="h4">{t('credentials')}</DialogTitle>
                <DialogContent>
                    {fullDeviceInfo && fullDeviceInfo.credentials && (
                        <Credentials device={fullDeviceInfo} credentialsObject={fullDeviceInfo.credentials} />
                    )}
                </DialogContent>
                <DialogActions>
                    <UtmsButton color="secondary" onClick={handleCloseModal} tabIndex={1}>
                        {t('close')}
                    </UtmsButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
